// client/src/components/CreateMapModal.js
import React, { useState, useEffect } from 'react';
import './CreateMapModal.css';
import { saveOfflineMap } from '../utils/offlineDb';

function CreateMapModal({ onClose, user, editMap, onEditSubmit }) {
  const [gameName, setGameName] = useState(editMap?.game_name || '');
  const [mapName, setMapName] = useState(editMap?.name || '');
  const [versionId, setVersionId] = useState(editMap?.version_id || '');
  const [mapFile, setMapFile] = useState(null);
  const [tipUrl, setTipUrl] = useState(editMap?.tip_url || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [gamesList, setGamesList] = useState([]);
  const [matchingGame, setMatchingGame] = useState(null);
  const [useOfficialMap, setUseOfficialMap] = useState(false);

  const [createMode, setCreateMode] = useState(user ? "online" : "offline");

  const [mapMode, setMapMode] = useState(editMap?.map_mode || 'standard');

  const [awaitingImagePick, setAwaitingImagePick] = useState(false);
  const [restoredMapData, setRestoredMapData] = useState(null);

  const uploaderId = user?.id;
  const userName = user?.username || (uploaderId ? `user${uploaderId}` : '');

  useEffect(() => {
    if (editMap) {
      setGameName(editMap.game_name || '');
      setMapName(editMap.name || '');
      setVersionId(editMap.version_id || '');
      setTipUrl(editMap.tip_url || '');
      setMapFile(null);
      if (editMap.map_mode) {
        setMapMode(editMap.map_mode);
      }
    }
  }, [editMap]);

  useEffect(() => {
    if (createMode === "online" && user) {
      fetch('/api/games')
        .then(res => res.json())
        .then(setGamesList)
        .catch(() => { });
    } else {
      setGamesList([]);
      setMatchingGame(null);
      setUseOfficialMap(false);
    }
  }, [createMode, user]);

  useEffect(() => {
    if (createMode === "online" && gamesList.length > 0) {
      const match = gamesList.find(
        g => g.name.toLowerCase().trim() === gameName.toLowerCase().trim()
      );
      setMatchingGame(match || null);
      setUseOfficialMap(false);
    } else {
      setMatchingGame(null);
    }
  }, [gameName, gamesList, createMode]);

  const generateRandomCode = () => {
    return Math.random().toString(36).substring(2, 6).toUpperCase();
  };

  const createMapDirectory = () => {
    const sanitizedGameName = gameName.trim().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '').toLowerCase();
    const sanitizedUserName = userName.trim().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '').toLowerCase();
    const randomCode = generateRandomCode();
    return `${sanitizedGameName}_${sanitizedUserName}_${randomCode}`;
  };

  const getImageSize = blob =>
    new Promise((resolve, reject) => {
      try {
        const url = URL.createObjectURL(blob);
        const img = new window.Image();
        img.onload = () => {
          resolve([img.naturalWidth, img.naturalHeight]);
          URL.revokeObjectURL(url);
        };
        img.onerror = reject;
        img.src = url;
      } catch (err) {
        reject(err);
      }
    });

  const handleCreateOfflineMap = async () => {
    setError(null);
    if (!gameName || (!mapFile && !useOfficialMap)) {
      setError('Please fill out all required fields.');
      return;
    }
    setLoading(true);

    let imageBlob = null;
    let imageName = "";

    if (mapFile) {
      if (!(mapFile instanceof File || mapFile instanceof Blob)) {
        setError("Invalid image file.");
        setLoading(false);
        return;
      }
      if (!mapFile.type.startsWith("image/")) {
        setError("File must be an image.");
        setLoading(false);
        return;
      }
      imageBlob = mapFile;
      imageName = mapFile.name || '';
    } else if (useOfficialMap && matchingGame?.default_map_url) {
      try {
        const resp = await fetch(matchingGame.default_map_url);
        if (!resp.ok) throw new Error("Could not fetch image from official source.");
        const blob = await resp.blob();
        if (!blob.type.startsWith("image/")) {
          setError("Official image is not a valid image.");
          setLoading(false);
          return;
        }
        imageBlob = blob;
        imageName = (matchingGame.name || "map") + ".png";
      } catch (err) {
        setError('Failed to fetch official map image.');
        setLoading(false);
        return;
      }
    }

    if (!imageBlob) {
      setError('Map image is required.');
      setLoading(false);
      return;
    }

    const offlineId = "map-" + Date.now();

    try {
      const image_size = await getImageSize(imageBlob);

      const bundle = {
        id: offlineId,
        meta: {
          name: mapName || gameName || "Offline Map",
          game: gameName,
          version_id: versionId,
          image_name: imageName,
          created_at: Date.now(),
        },
        image: imageBlob,
        image_size,
        pois: [],
        poi_categories: [],
        poi_types: [],
        zones: [],
        lastEdited: Date.now()
      };

      await saveOfflineMap(offlineId, bundle);
      setLoading(false);
      onClose();
      window.location.href = `/offline-map/${offlineId}`;
    } catch (err) {
      setError('Failed to save offline map.');
      setLoading(false);
    }
  };

  const handlePickedImageForRestore = async (e) => {
    if (!restoredMapData) return;
    if (!e.target.files.length) return;

    const file = e.target.files[0];
    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result;
        const res = await fetch(dataUrl);
        const blob = await res.blob();

        const image_size = await getImageSize(blob);

        restoredMapData.image = blob;
        restoredMapData.image_url = dataUrl;
        restoredMapData.image_size = image_size;
        restoredMapData.meta.image_name = file.name;

        await saveOfflineMap(restoredMapData.id, restoredMapData);

        setLoading(false);
        setAwaitingImagePick(false);
        onClose();
        window.location.href = `/offline-map/${restoredMapData.id}`;
      };
      reader.readAsDataURL(file);
    } catch (err) {
      setError('Failed to load image file.');
      setLoading(false);
      setAwaitingImagePick(false);
    }
  };

  const handleRestoreBackup = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = async (event) => {
      if (!event.target.files.length) return;
      const file = event.target.files[0];
      setLoading(true);
      setError(null);
      try {
        const text = await file.text();
        const mapDataJson = JSON.parse(text);

        setRestoredMapData(mapDataJson);
        setAwaitingImagePick(true);
        setLoading(false);
      } catch (err) {
        setError("Failed to restore backup: " + err.message);
        setLoading(false);
      }
    };
    input.click();
  };

  const renderImagePickPrompt = () => (
    <div className="map-modal-backdrop" style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.75)',
      zIndex: 1100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'inherit'
    }}>
      <div style={{
        background: '#151619',
        color: '#f4f4f6',
        padding: '32px 24px 20px',
        borderRadius: '14px',
        maxWidth: 420,
        boxShadow: '0 6px 32px #000b, 0 0 0 2px #23272b',
        textAlign: 'center'
      }}>
        <h2 style={{ marginBottom: 22, fontWeight: 'bold', fontSize: '1.35em' }}>
          Select Map Image File
        </h2>
        <p style={{ marginBottom: 18 }}>
          Please select the map image file to associate with the restored backup.
        </p>
        <input
          type="file"
          accept="image/*"
          onChange={handlePickedImageForRestore}
          style={{ marginBottom: 12 }}
        />
        <button
          onClick={() => {
            setAwaitingImagePick(false);
            setRestoredMapData(null);
            setError(null);
          }}
          style={{
            background: '#555',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 18px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const handleUploadOrEdit = async () => {
    setError(null);

    if (!gameName || (!mapFile && !editMap && !useOfficialMap)) {
      setError('Please fill out all required fields.');
      return;
    }
    if (!uploaderId) {
      setError('You must be logged in.');
      return;
    }

    const formData = new FormData();
    formData.append('game_name', gameName);
    formData.append('display_name', mapName);
    formData.append('version_id', versionId);
    formData.append('uploader_id', uploaderId);

    const isTabletop = mapMode === 'tabletop';
    formData.append('map_mode', mapMode);
    formData.append('is_tabletop', isTabletop ? '1' : '0');
    formData.append('mode', isTabletop ? 'dnd' : 'default');

    if (tipUrl && tipUrl.trim() !== '') {
      formData.append('tip_url', tipUrl);
    }
    if (mapFile) {
      formData.append('map', mapFile);
    }
    if (useOfficialMap && matchingGame) {
      formData.append('use_official_map', '1');
      formData.append('official_game_slug', matchingGame.slug);
    }
    if (!editMap) {
      const mapDirectory = createMapDirectory();
      formData.append('map_directory', mapDirectory);
    }

    setLoading(true);

    try {
      let url = '/api/upload-map';
      let method = 'POST';

      if (editMap) {
        url = `/api/upload-map/${editMap.id}`;
        method = 'PUT';
      }

      const res = await fetch(url, {
        method,
        body: formData,
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Upload failed.');
      const data = await res.json();

      if (editMap && onEditSubmit) {
        onEditSubmit();
      } else {
        onClose();

        const isVtt =
          data.mode === 'dnd' ||
          data.map_mode === 'tabletop' ||
          data.is_tabletop === 1 ||
          data.is_tabletop === '1';

        const basePath = isVtt ? '/dnd/map' : '/map';

        if (data.id && data.slug && data.uploader_username) {
          window.location.href = `${basePath}/${data.slug}_${data.uploader_username}_${data.id}`;
        } else if (data.id) {
          window.location.href = `${basePath}/${data.id}`;
        } else if (data.map_directory) {
          window.location.href = `${basePath}/${data.map_directory}`;
        } else {
          window.location.reload();
        }
      }
    } catch (err) {
      setError('Upload failed.');
    } finally {
      setLoading(false);
    }
  };

  const modeSelector = user ? (
    <div style={{
      display: "flex", justifyContent: "center", gap: 10, marginBottom: 18
    }}>
      <button
        onClick={() => setCreateMode("online")}
        disabled={loading}
        style={{
          background: createMode === "online" ? "#3385ff" : "#23272b",
          color: "#fff",
          border: "none",
          borderRadius: 7,
          padding: "9px 24px",
          fontWeight: 700,
          fontSize: 16,
          boxShadow: createMode === "online" ? "0 1px 8px #3385ff44" : "none",
          opacity: loading ? 0.5 : 1,
          cursor: loading ? "not-allowed" : "pointer"
        }}
      >
        Upload to Point Atlas (Online)
      </button>
      <button
        onClick={() => setCreateMode("offline")}
        disabled={loading}
        style={{
          background: createMode === "offline" ? "#4bbb64" : "#23272b",
          color: "#fff",
          border: "none",
          borderRadius: 7,
          padding: "9px 24px",
          fontWeight: 700,
          fontSize: 16,
          boxShadow: createMode === "offline" ? "0 1px 8px #67e49344" : "none",
          opacity: loading ? 0.5 : 1,
          cursor: loading ? "not-allowed" : "pointer"
        }}
      >
        Create Local Map (Offline)
      </button>
    </div>
  ) : null;

  return (
    <>
      {awaitingImagePick && renderImagePickPrompt()}
      <div
        className="map-modal-backdrop"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.75)',
          zIndex: 1000
        }}
      >
        <div
          className="map-modal-container"
          onClick={e => e.stopPropagation()}
          style={{
            background: '#151619',
            color: '#f4f4f6',
            padding: '32px 24px 20px',
            borderRadius: '14px',
            maxWidth: 420,
            margin: '60px auto',
            boxShadow: '0 6px 32px #000b, 0 0 0 2px #23272b',
            position: 'relative',
            fontFamily: 'inherit'
          }}
        >
          <h2 className="map-modal-title" style={{
            fontSize: '1.35em',
            fontWeight: 'bold',
            marginBottom: 22,
            color: '#fff',
            textAlign: 'center',
            letterSpacing: '0.02em'
          }}>
            {editMap ? "Edit Map" : "Create New Map"}
          </h2>

          {createMode === "offline" && (
            <>
              <div style={{
                background: "#23272b",
                color: "#ffe187",
                border: "1.5px solid #444",
                borderRadius: 7,
                padding: "11px 14px",
                fontSize: "1em",
                margin: "0 0 18px 0",
                boxShadow: "0 1px 8px #1118"
              }}>
                <b>Note:</b> Your map will be saved only to <b>this browser</b>. It won’t be available on other devices or browsers, and may be lost if you clear your site data.
                <br />
                <span style={{ color: "#ffc2c2" }}>
                  Not supported in Internet Explorer or very old browsers.
                </span>
              </div>

              <button
                onClick={handleRestoreBackup}
                disabled={loading}
                style={{
                  background: '#ffa500',
                  color: '#000',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 18px',
                  fontSize: '1em',
                  fontWeight: 'bold',
                  marginBottom: 18,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  width: '100%',
                  textAlign: 'center'
                }}
                title="Restore map from backup JSON file"
              >
                {loading ? "Restoring..." : "Restore Map from Backup"}
              </button>
            </>
          )}

          {modeSelector}

          <input
            type="text"
            className="map-modal-input"
            placeholder="Game Name"
            value={gameName}
            style={{
              background: '#23272b',
              color: '#fff',
              border: '1px solid #313639',
              borderRadius: 5,
              fontSize: '1em',
              padding: '9px 11px',
              margin: '14px 0 0 0'
            }}
            onChange={e => setGameName(e.target.value)}
          />

          {createMode === "online" && matchingGame && !editMap && (
            <div className="official-map-suggestion" style={{
              margin: '18px 0 12px',
              textAlign: 'center',
              background: '#f7f9ff',
              border: '1.5px solid #cce2ff',
              color: '#1c2333',
              borderRadius: 8,
              padding: '16px 12px 12px',
              boxShadow: '0 2px 12px #cce2ff26'
            }}>
              <div style={{ marginBottom: 6, fontWeight: 600, fontSize: '1.03em' }}>
                A high-resolution map image for <span style={{ color: "#49f" }}>{matchingGame.name}</span> is available.<br />
                Would you like to use it as your map background?
              </div>
              {useOfficialMap && matchingGame.default_map_url && (
                <div className="official-map-preview">
                  <img
                    src={matchingGame.default_map_url}
                    alt="Official map preview"
                    style={{
                      maxWidth: 180,
                      maxHeight: 100,
                      borderRadius: 4,
                      margin: '6px 0',
                      boxShadow: '0 1px 6px #0001'
                    }}
                  />
                  <div style={{ fontSize: '0.8em', color: '#888' }}>This image will be used for your map.</div>
                </div>
              )}
              <div style={{ marginTop: 4 }}>
                <button
                  className={`official-map-yes ${useOfficialMap ? 'active' : ''}`}
                  type="button"
                  onClick={() => { setUseOfficialMap(true); setMapFile(null); }}
                  style={{
                    background: useOfficialMap ? '#3385ff' : '#ecefff',
                    color: useOfficialMap ? '#fff' : '#181a1b',
                    border: 'none',
                    borderRadius: 5,
                    padding: '6px 20px',
                    marginRight: 7,
                    fontWeight: 600,
                    fontSize: '1em',
                    cursor: 'pointer',
                    minWidth: 48,
                    transition: 'background 0.13s'
                  }}
                >
                  Yes
                </button>
                <button
                  className={`official-map-no ${!useOfficialMap ? 'active' : ''}`}
                  type="button"
                  onClick={() => setUseOfficialMap(false)}
                  style={{
                    background: !useOfficialMap ? '#3385ff' : '#ecefff',
                    color: !useOfficialMap ? '#fff' : '#181a1b',
                    border: 'none',
                    borderRadius: 5,
                    padding: '6px 20px',
                    fontWeight: 600,
                    fontSize: '1em',
                    cursor: 'pointer',
                    minWidth: 48,
                    transition: 'background 0.13s'
                  }}
                >
                  No
                </button>
              </div>
            </div>
          )}

          <input
            type="text"
            className="map-modal-input"
            placeholder="Map Name (optional)"
            value={mapName}
            style={{
              background: '#23272b',
              color: '#fff',
              border: '1px solid #313639',
              borderRadius: 5,
              fontSize: '1em',
              padding: '9px 11px',
              margin: '14px 0 0 0'
            }}
            onChange={e => setMapName(e.target.value)}
          />

          <input
            type="text"
            className="map-modal-input"
            placeholder="Version ID (optional)"
            value={versionId}
            style={{
              background: '#23272b',
              color: '#fff',
              border: '1px solid #313639',
              borderRadius: 5,
              fontSize: '1em',
              padding: '9px 11px',
              margin: '14px 0 0 0'
            }}
            onChange={e => setVersionId(e.target.value)}
          />

          {createMode === "online" && (
            <>
              <input
                type="url"
                className="map-modal-input"
                placeholder="Tip URL (optional, e.g. https://www.buymeacoffee.com/yourname)"
                value={tipUrl}
                style={{
                  background: '#23272b',
                  color: '#fff',
                  border: '1px solid #313639',
                  borderRadius: 5,
                  fontSize: '1em',
                  padding: '9px 11px',
                  margin: '14px 0 0 0'
                }}
                onChange={e => setTipUrl(e.target.value)}
              />

              <div style={{ marginTop: 14 }}>
                <div style={{ marginBottom: 6, fontSize: '0.95em', color: '#ddd' }}>
                  Map Type
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setMapMode('standard')}
                    disabled={loading}
                    style={{
                      background: mapMode === 'standard' ? '#3385ff' : '#23272b',
                      color: '#fff',
                      border: '1px solid #313639',
                      borderRadius: 6,
                      padding: '6px 14px',
                      fontSize: '0.95em',
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      boxShadow: mapMode === 'standard' ? '0 1px 6px #3385ff55' : 'none'
                    }}
                  >
                    Standard Map
                  </button>
                  <button
                    type="button"
                    onClick={() => setMapMode('tabletop')}
                    disabled={loading}
                    style={{
                      background: mapMode === 'tabletop' ? '#4bbb64' : '#23272b',
                      color: '#fff',
                      border: '1px solid #313639',
                      borderRadius: 6,
                      padding: '6px 14px',
                      fontSize: '0.95em',
                      fontWeight: 600,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      boxShadow: mapMode === 'tabletop' ? '0 1px 6px #4bbb6455' : 'none'
                    }}
                  >
                    Tabletop / VTT
                  </button>
                </div>
                <div style={{ marginTop: 6, fontSize: '0.8em', color: '#888' }}>
                  Tabletop maps can use tokens, fog of war, lighting, and other VTT tools.
                </div>
              </div>
            </>
          )}

          {!useOfficialMap && (
            <>
              <label className="map-modal-label" style={{
                color: '#ddd',
                margin: '14px 0 3px 0',
                display: 'block'
              }}>Map Image:</label>
              <input
                type="file"
                className="map-modal-input"
                accept="image/*"
                style={{
                  background: 'none',
                  color: '#cce2ff',
                  border: '1px solid #313639',
                  borderRadius: 5,
                  fontSize: '1em',
                  padding: '8px 0',
                  margin: '0'
                }}
                onChange={e => setMapFile(e.target.files[0])}
              />
              {mapFile && <div className="map-modal-file-info" style={{
                color: '#9ad',
                fontSize: '0.92em',
                margin: '3px 0 0 0'
              }}>Selected: {mapFile.name}</div>}
            </>
          )}

          {error && <div className="map-modal-error" style={{
            color: '#ff6565',
            fontWeight: 'bold',
            marginTop: 8,
            textAlign: 'center'
          }}>{error}</div>}

          <div className="map-modal-buttons" style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 20
          }}>
            <button className="map-modal-button cancel" onClick={onClose} style={{
              background: '#23272b',
              color: '#fff',
              border: '1px solid #495158',
              borderRadius: 6,
              padding: '8px 18px',
              fontSize: '1em',
              fontWeight: 'bold',
              marginLeft: 10,
              cursor: 'pointer'
            }}>Cancel</button>
            {createMode === "online" && user && (
              <button
                className="map-modal-button upload"
                onClick={handleUploadOrEdit}
                disabled={loading}
                style={{
                  background: '#3385ff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 18px',
                  fontSize: '1em',
                  fontWeight: 'bold',
                  marginLeft: 10,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? (editMap ? "Updating..." : "Uploading...") : (editMap ? "Update Map" : "Upload")}
              </button>
            )}
            {(createMode === "offline" || !user) && (
              <button
                className="map-modal-button upload"
                onClick={handleCreateOfflineMap}
                disabled={loading}
                style={{
                  background: '#4bbb64',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 18px',
                  fontSize: '1em',
                  fontWeight: 'bold',
                  marginLeft: 10,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? "Saving..." : "Create Local Map"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default CreateMapModal;
